// English
import type { Messages } from "./pt-BR";

const en: Messages = {
  "common.loading": "Loading…",
  "common.close": "Close",
  "common.back": "Back",
  "common.theme": "Switch theme",
  "common.language": "Language",
  "common.error": "Something went wrong. Please try again.",

  "auth.login": "Sign in",
  "auth.loginField": "Username or email",
  "auth.password": "Password",
  "auth.forgotLink": "Forgot my password",
  "auth.newHere": "New here?",
  "auth.createAccount": "Create account",
  "auth.haveAccount": "Already have an account?",
  "auth.username": "Username",
  "auth.usernameHint": "3 to 20 characters: lowercase letters, numbers and _. Used to sign in.",
  "auth.email": "Email",
  "auth.emailHint": "We'll send a confirmation link. It's used to recover your password.",
  "auth.nickname": "Nickname",
  "auth.nicknameHint": "How you appear in matches (3 to 16 characters).",
  "auth.passwordHint": "At least 8 characters.",
  "auth.confirm": "Confirm password",
  "auth.err.login": "Enter your username or email.",
  "auth.err.password": "Enter your password.",
  "auth.err.username": "3 to 20 characters: lowercase letters, numbers and _.",
  "auth.err.email": "Invalid email.",
  "auth.err.nickname": "Your nickname needs 3 to 16 characters.",
  "auth.err.passwordShort": "Use at least 8 characters.",
  "auth.err.confirm": "Passwords don't match.",
  "auth.forgot": "Recover password",
  "auth.forgotText": "Enter your email. If there's an account, we'll send a link valid for 30 minutes.",
  "auth.sendLink": "Send link",
  "auth.backToLogin": "Back to sign in",
  "auth.forgotSent": "If the email is registered, the link will arrive shortly. Check your spam folder too.",
  "auth.newPassword": "New password",
  "auth.newPasswordTitle": "Create a new password",
  "auth.savePassword": "Save password",
  "auth.passwordChanged": "Password changed! Sign in with your new password.",
  "auth.linkIncomplete": "Incomplete link. Request a new one in Recover password.",
  "auth.verifyTitle": "Confirm email",
  "auth.verifying": "Confirming…",
  "auth.goMenu": "Go to menu",
  "auth.verified": "Email confirmed!",
  "auth.verifyFailed": "Invalid or expired link. Request a new confirmation link from the menu.",

  "err.offline": "No connection to the server. Check your internet.",
  "err.invalidCredentials": "Incorrect username, email or password.",
  "err.tooManyAttempts": "Too many attempts. Wait a few minutes and try again.",
  "err.rateLimited": "Too many requests in a row. Please wait a moment.",
  "err.usernameTaken": "That username is already taken.",
  "err.emailTaken": "There's already an account with that email.",
  "err.invalidToken": "Invalid or expired link. Request a new one.",
  "err.banned": "This account is suspended.",
  "err.nicknameCooldown": "You can only change your nickname every {days} days.",
  "err.alreadyVerified": "Your email is already confirmed.",
  "err.unauthenticated": "Your session expired. Please sign in again.",
  "err.validation": "Check the highlighted fields.",
  "err.invalidDeck": "Invalid deck: 1 card of each tier and at most 1 per category (2 Energies).",
  "err.notOwned": "You don't own that item yet.",
  "err.alreadyOwned": "You already own that item.",
  "err.paymentProvider": "Mercado Pago didn't respond. Try again in a moment.",

  "title.set": "Set 1 · Kanto",
  "title.start": "Start",
  "title.tap": "Tap to start",
  "title.credits": "Credits",

  "menu.title": "Menu",
  "menu.accountLevel": "Account level {n}",
  "menu.logout": "Sign out",
  "menu.welcome": "Account created! Welcome, {name}.",
  "menu.confirmEmail": "Confirm your email ({email}) so you can recover your password if you forget it.",
  "menu.resend": "Resend link",
  "menu.play": "Play",
  "menu.play.sub": "Normal match · up to 8 players",
  "menu.ranked": "Ranked",
  "menu.ranked.sub": "Season 1 · Kanto",
  "menu.ranked.locked": "Confirm your email to unlock",
  "menu.deck": "Card deck",
  "menu.deck.sub": "Gold · Silver · Copper",
  "menu.ranking": "Ranking",
  "menu.ranking.sub": "Season leaders and history",
  "menu.collection": "Collection",
  "menu.collection.sub": "Shinies",
  "menu.shop": "VIP Shop",
  "menu.shop.sub": "Crystals and boards",
  "menu.friends": "Play with friends",
  "menu.friends.text": "Agree on a code and everyone joins the same room. Whoever joins first decides when to start.",
  "menu.friends.placeholder": "Room code (e.g. PALLET)",
  "menu.friends.join": "Join",
  "menu.linkSent": "Link sent! Check your inbox (and spam).",
  "menu.err.verify": "Confirm your email to play ranked.",
  "menu.err.connect": "Couldn't connect to the game server.",
  "menu.err.code": "Use a code with at least 3 letters or numbers.",
  "menu.rejoin": "You have a match in progress.",
  "menu.rejoin.button": "Back to the match",
  "menu.rejoin.expired": "The match has ended or the time to rejoin expired.",

  "lobby.title": "Finding a match",
  "lobby.players": "Players",
  "lobby.playersCount": "Players ({n}/{max})",
  "lobby.start": "Start now",
  "lobby.leave": "Leave queue",
  "lobby.you": "you",
  "lobby.cancelled": "Match cancelled: not enough players. Go back to the menu and try again.",
  "lobby.hint.solo": "You can start alone: PvP rounds will be against in-game trainers. Or wait for more people to join.",
  "lobby.hint.host": "You own the room: start whenever you want.",
  "lobby.hint.wait": "Waiting for the room owner to start (or the room to fill up).",

  "match.level": "Lv. {n}",
  "match.badges": "Badges",
  "match.quit": "Leave match",
  "match.trainers": "Trainers",
  "match.traits": "Synergies",
  "match.traits.empty": "Place units on the board to activate synergies.",
  "match.lockShop": "Lock shop",
  "match.free": "free ({n})",
  "match.round.pve": "Wild",
  "match.round.pvp": "Battle",
  "match.round.safari": "Safari Zone",
  "match.round.gym": "Gym Leader",
  "match.err.not-enough-gold": "Not enough gold",
  "match.err.bench-full": "Bench is full",
  "match.err.board-full": "Board is full for your level. Level up with XP",
  "match.err.max-level": "Max level",
  "match.err.items-full": "This unit already has 3 items",
  "match.err.not-planning": "Wait for the planning phase",
  "match.err.rate-limited": "Easy! Too many actions per second",
  "match.range": "range {n}",
  "match.mana": "mana",
  "match.sell": "Sell +{n}",
  "match.equip": "Equip",
  "match.equipHint": "Now tap the unit that will receive the component",
  "match.badge.choose": "Choose the badge bonus.",
  "match.branch.choose": "Choose the evolution",
  "match.backpack": "Backpack: choose a component",
  "match.safari.text": "Choose a Pokémon (with a component). The player with the least HP picks first.",
  "match.safari.picked": "Choice saved. You can change it until time runs out.",
  "match.safari.toast": "Safari Zone: choose a Pokémon!",
  "match.gym.toast": "{leader} wants to battle! Win to choose the {badge} bonus.",
  "match.quit.title": "Leave the match?",
  "match.quit.text": "Your board stays in the match, idle, until it ends. You can come back from the menu.",
  "match.quit.yes": "Leave",
  "match.quit.no": "Keep playing",
  "match.end.league": "You won the Kanto League!",
  "match.end.journey": "End of the journey",
  "match.end.top4": "Top 4!",
  "match.end.over": "Game over",
  "match.end.points": "{n} points",
  "match.end.xp": "+{n} account XP",
  "match.end.levelUp": "Reached level {n}!",
  "match.end.newCard": "New card:",
  "match.end.shiny": "New shiny unlocked! Check your Collection.",
  "match.end.menu": "Back to menu",
  "match.reconnecting": "Connection lost. Reconnecting…",
  "match.reconnectFailed": "Couldn't get back into the match.",
  "match.vs.wild": "Wild / Leader",
  "match.vs.npc": "In-game trainer",
  "match.combat": "Combat",
  "match.combatVs": "Combat · vs {name}",

  "tip.component": "Component",
  "tip.item": "Item",
  "tip.combine": "Put 2 components on the same unit to make an item:",
  "tip.onBoard": "{n} on the board",
  "stat.adPct": "+{v}% attack damage",
  "stat.ap": "+{v} ability power",
  "stat.asPct": "+{v}% attack speed",
  "stat.critPct": "+{v}% crit chance",
  "stat.armor": "+{v} armor",
  "stat.mr": "+{v} magic resist",
  "stat.hp": "+{v} HP",
  "stat.startMana": "+{v} starting mana",

  "tier.gold": "Gold",
  "tier.silver": "Silver",
  "tier.copper": "Copper",
  "deck.title": "Card deck",
  "deck.yours": "Your deck · Kanto",
  "deck.empty": "Empty",
  "deck.emptyHint": "Pick a card of this tier from the collection below.",
  "deck.save": "Save deck",
  "deck.rules": "1 card of each tier · up to {energy} Energies and 1 of each other category",
  "deck.allUnlocked": " · all unlocked (level {n}+)",
  "deck.unlockAt": " · all unlock at level {n}",
  "deck.collection": "Collection",
  "deck.all": "All",
  "deck.locked": "Earn it by leveling up",
  "deck.saved": "Deck saved! It applies from your next match.",
  "deck.needAll": "Pick a card for each tier (Gold, Silver and Copper).",

  "ranking.title": "Ranking",
  "ranking.noSeason": "No active season right now.",
  "ranking.endsIn": "ends in {n} day(s)",
  "ranking.you": "You:",
  "ranking.unranked": "No ranked matches",
  "ranking.placement": "{n} placement match(es) (double points)",
  "ranking.trainer": "Trainer",
  "ranking.games": "Matches",
  "ranking.elo": "Rank",
  "ranking.gamesWins": "{n} ({w} wins)",
  "ranking.empty": "Nobody has played ranked yet. Be the first!",
  "ranking.history": "Your recent matches",
  "ranking.placeOf": "#{p} of {n}",
  "ranking.mode.ranked": "Ranked",
  "ranking.mode.custom": "With friends",
  "ranking.mode.normal": "Normal",
  "ranking.noMatches": "No matches yet.",
  "ranking.pts": "{n} pts",
  "rank.pokeball": "Poké Ball",
  "rank.greatball": "Great Ball",
  "rank.ultraball": "Ultra Ball",
  "rank.masterball": "Master Ball",

  "collection.title": "Collection",
  "collection.text": "Shinies are cosmetic only: everyone in the match sees them. Buy them with Pokédollars or earn them by leveling your account (every 5 levels) and by your best tier in the season.",
  "collection.showOwned": "Show only yours",
  "collection.showingOwned": "Showing only yours",
  "collection.count": "{n} of {total}",
  "collection.tapToEquip": "Tap to equip",
  "rarity.common": "Common",
  "rarity.rare": "Rare",
  "rarity.epic": "Epic",
  "rarity.legendary": "Legendary",

  "shop.title": "VIP Shop",
  "shop.crystals": "crystals",
  "shop.tab.boards": "Boards",
  "shop.tab.crystals": "Crystals",
  "shop.tab.history": "History",
  "shop.boards.explain": "Boards change the match arena. In a battle between two players, both see the board of whoever has more HP; the next time they meet, the other one's. Cosmetic only: no advantage.",
  "shop.default": "Default",
  "shop.default.desc": "The classic blue and red stadium, free for everyone.",
  "shop.equip": "Equip",
  "shop.equipped": "Equipped",
  "shop.animated": "Animated",
  "shop.preview": "Preview",
  "shop.confirmBuy": "Confirm? {price}",
  "shop.bought": "{name} bought and equipped!",
  "shop.notEnough": "Not enough crystals. Check the crystal packs.",
  "shop.crystals.explain": "Crystals are donations that help keep the server running. They only buy cosmetics and never give an in-game advantage.",
  "shop.bonus": "bonus",
  "shop.donate": "Donate {price}",
  "shop.mp.note": "Payment processed by Mercado Pago (Pix, card or boleto). Crystals are added as soon as the payment is approved.",
  "shop.devMode": "Test mode: nothing is charged (server without Mercado Pago configured).",
  "shop.paymentsOff": "Donations are unavailable right now.",
  "shop.donation.ok": "Thanks for your donation! {n} crystals added.",
  "shop.donation.waiting": "Waiting for payment confirmation…",
  "shop.donation.failed": "The payment wasn't completed. Nothing was charged.",
  "shop.history.empty": "No donations yet.",
  "shop.history.date": "Date",
  "shop.history.value": "Amount",
  "shop.history.status": "Status",
  "shop.status.pending": "Pending",
  "shop.status.in_process": "In review",
  "shop.status.approved": "Approved",
  "shop.status.rejected": "Declined",
  "shop.status.cancelled": "Cancelled",
  "shop.status.refunded": "Refunded",
  "shop.status.charged_back": "Charged back",
  "shop.devCheckout.title": "Test checkout",
  "shop.devCheckout.text": "Simulate the payment result for this donation.",
  "shop.devCheckout.approve": "Approve payment",
  "shop.devCheckout.reject": "Decline",

  "credits.title": "Credits",
  "credits.sprites": "Pokémon sprites",
  "credits.spritesText": "Animations from {source}, license {license}. Art by:",
  "credits.items": "Items, data and fonts",
  "credits.itemsText": "Item icons, names and species data: {source} / PokéAPI.",
  "credits.fonts": "Fonts: Jersey 10 and DotGothic16 (SIL Open Font License).",
  "credits.trademark": "Pokémon and all related names are trademarks of Nintendo, Game Freak and Creatures. Fan project, not affiliated.",
  "credits.error": "Couldn't load the credits.",

  // efeitos de combate
  "fx.effective": "super effective",
  "fx.shield": "shield",
  "fx.stun": "stunned",
  "fx.sleep": "zzz",
  "fx.freeze": "frozen",
  "fx.confuse": "confused",
  "fx.burn": "burning",
  "fx.poison": "poisoned",
  "fx.intangible": "intangible",
  // foto de perfil
  "avatar.change": "Change profile picture",
  "avatar.title": "Choose your profile picture",
  "avatar.search": "Search Pokémon",
  "avatar.saved": "Profile picture updated!",
  "market.title": "Poké Mart",
  "market.tab.special": "Special",
  "market.tab.item": "Full items",
  "market.tab.component": "Components",
  "market.explain": "Items you buy go into your item pool: in match rewards, full items from your pool can drop already built (6% per item, up to 30%) and pool components drop twice as often.",
  "market.inPool": "{n} in your pool.",
  "market.owned": "In your pool",
  "market.bought": "{name} added to your item pool!",
  "match.end.pokedollars": "+{n} Pokédollars",
  "menu.market": "Poké Mart",
  "menu.market.sub": "Items for Pokédollars",
  "pdx.confirm": "Buy? {price}",
  "pdx.explain": "Pokédollars: earn 0 to 100 per match (1st place = 100). Buy cards, shinies and items.",
  "pdx.notEnough": "Not enough Pokédollars. Play matches to earn more.",
  "tip.special": "Special",
  "tip.specialHint": "Only from the Poké Mart: drops ready-made in matches and takes 2 slots.",
  "changelog.title": "What's new",
  "changelog.version": "Version {v}",
  "changelog.new": "New",
  "changelog.link": "What's new in version {v}",
  "tip.unit.cost": "Cost {n}",
  "tip.unit.evolves": "Evolution:",
  "tip.unit.branch": "your choice",
  "tip.unit.owned": "You already have {n} cop(ies): 3 copies make a ★2.",
  "social.title": "Friends and chat",
  "social.global": "Global",
  "social.friendsTab": "Friends",
  "social.say": "Type a message…",
  "social.send": "Send",
  "social.empty": "No messages yet.",
  "social.addPlaceholder": "Friend's username",
  "social.add": "Add",
  "social.incoming": "Friend requests",
  "social.outgoing": "Sent requests",
  "social.friends": "Friends ({n})",
  "social.noFriends": "Add friends by username.",
  "social.remove": "Remove",
  "social.requestSent": "Request sent!",
  "social.nowFriends": "You're now friends!",
  "social.roomChat": "Room chat",
  "social.err.notFound": "User not found.",
  "social.err.self": "You can't add yourself.",
  "social.err.already": "You're already friends.",
  "social.err.notFriends": "You can only chat with friends.",
  "menu.creche": "Daycare",
  "menu.creche.sub": "Raise and unlock Pokémon",
  "creche.title": "Daycare",
  "creche.explain": "Raise a Pokémon from a locked line to level {max} to unlock that line in your match shop. Care for it and play matches: it earns XP with you.",
  "creche.level": "Level {n}/{max}",
  "creche.unlocks": "unlocks the {name} line",
  "creche.xp": "XP",
  "creche.belly": "Belly",
  "creche.happiness": "Happiness",
  "creche.mood.hungry": "It's hungry! Feed it to earn full XP.",
  "creche.mood.sad": "It's sad… play with it.",
  "creche.mood.joy": "Very happy! Earns full XP in matches.",
  "creche.mood.ok": "It's fine. Earns full XP in matches.",
  "creche.feed": "Feed",
  "creche.play": "Play",
  "creche.train": "Train",
  "creche.tips": "Each match gives 20 XP (10 if hungry or sad). Play: every 30 min. Train: every 2 h, gives 30 XP.",
  "creche.release": "Release this Pokémon",
  "creche.releaseConfirm": "Tap again to release (progress is lost)",
  "creche.choose": "Choose a Pokémon to raise. When it reaches max level, its line starts showing in your match shop.",
  "creche.allDone": "You've unlocked every line! Your Pokédex is complete.",
  "creche.dex": "Kanto Pokédex: {n}/{total} unlocked",
  "creche.adopted": "{name} arrived at the Daycare!",
  "creche.unlocked": "{name} is now in your shop!",
  "creche.gotXp": "+{n} XP",
  "creche.err.not-hungry": "It isn't hungry right now.",
  "creche.err.too-hungry": "It's too hungry. Feed it first.",
  "creche.err.too-sad": "It's too sad to train. Play with it.",
  "creche.err.cooldown": "It needs to rest a bit.",
  "menu.missions": "Missions",
  "menu.missions.sub": "Daily ₽ rewards",
  "missions.title": "Daily missions",
  "missions.intro": "Complete missions by playing matches and claim the reward. New missions in",
  "missions.resetsIn": "{t}",
  "missions.streak": "{n}-day login streak",
  "missions.best": "Best: {n}",
  "missions.completedTotal": "{n} missions completed",
  "missions.seeRanking": "See ranking",
  "missions.claimed": "Claimed",
  "missions.got": "+₽ {n}!",
  "missions.hint": "Progress counts at the end of each match.",
  "missions.doneInMatch": "{n} mission(s) completed! Claim your reward.",
  "missions.k.synergy": "Get {n} {trait} in a match",
  "missions.k.play": "Play {n} matches",
  "missions.k.win": "Win {n} match(es)",
  "missions.k.tophalf": "Finish in the top half {n} times",
  "missions.k.ranked-play": "Play {n} ranked match(es)",
  "missions.k.ranked-win": "Win {n} ranked match(es)",
  "missions.k.pvp-wins": "Win {n} rounds against players",
  "missions.k.star3": "Get a 3-star Pokémon",
  "missions.k.items": "Have {n} completed items on your board",
  "missions.k.level": "Reach level {n} in a match",
  "ranking.tab.ranked": "Ranked",
  "ranking.tab.level": "Level",
  "ranking.tab.missions": "Missions",
  "ranking.tab.streak": "Streak",
  "ranking.about.level": "Account level: goes up with XP from every match.",
  "ranking.about.missions": "Total daily missions completed and claimed.",
  "ranking.about.streak": "Consecutive days logging in. Miss a day and the streak restarts.",
  "ranking.col.level": "Level",
  "ranking.col.missions": "Missions",
  "ranking.col.streak": "Days",
  "ranking.levelValue": "Lv. {n}",
  "ranking.missionsValue": "{n}",
  "ranking.streakValue": "{n} days",
  "ranking.bestStreak": "best {n}",
  "match.migrating": "Updating the server… taking you back to the match.",
  "queue.searching": "Finding a match…",
  "queue.searchingRanked": "Finding a ranked match…",
  "queue.starting": "Joining the queue",
  "queue.progress": "{time} · {n} in queue · range ±{range}",
  "queue.cancel": "Cancel",
  "match.vs.ghost": "{name} (recorded board)",
  "common.sound": "Sound and vibration",
  "menu.achievements": "Achievements",
  "menu.achievements.sub": "Titles and frames",
  "ach.title": "Achievements",
  "ach.unlocked": "unlocked",
  "ach.titleLabel": "Title",
  "ach.frameLabel": "Frame",
  "ach.none": "None",
  "ach.secretName": "Secret achievement",
  "ach.secretDesc": "Keep playing to find out.",
  "ach.frameReward": "frame",
  "ach.saved": "Profile updated.",
  "ach.cat.jornada": "Journey",
  "ach.cat.colecao": "Collection",
  "ach.cat.habilidade": "Skill",
  "ach.cat.secreta": "Secret",
  "ach.frame.ouro": "Gold",
  "ach.frame.prata": "Silver",
  "ach.frame.master": "Master Ball",
  "ach.frame.pokedex": "Pokédex",
  "ach.frame.shiny": "Shiny",
  "ach.toast": "Achievement: {name}!",
  "ach.inMatch": "{n} new achievement(s)!",
  "report.button": "Report or block",
  "report.title": "Report {name}",
  "report.why": "What happened?",
  "report.reason.nickname": "Offensive nickname",
  "report.reason.chat": "Offensive chat or spam",
  "report.reason.afk": "Left / idle",
  "report.reason.cheating": "Cheating",
  "report.reason.other": "Other",
  "report.details": "Details (optional)",
  "report.alsoBlock": "Also block this person",
  "report.note": "We keep recent messages as evidence. False reports may be penalized.",
  "report.send": "Report",
  "report.blockOnly": "Just block",
  "report.unblock": "Unblock",
  "report.thanks": "Thanks! Moderators will review it.",
  "social.muted": "Your chat is muted until {when}.",
  "mod.title": "Moderation",
  "mod.open": "Open",
  "mod.closed": "Resolved",
  "mod.by": "by {name} · {when}",
  "mod.counts": "{open} open report(s) · {people} different people",
  "mod.mutedUntil": "muted until {when}",
  "mod.bannedUntil": "suspended until {when}",
  "mod.nickAtTime": "Nickname at the time: {name}",
  "mod.evidence": "Evidence ({n} messages)",
  "mod.room": "room",
  "mod.noEvidence": "No saved messages.",
  "mod.dismiss": "Dismiss",
  "mod.mute24": "Mute 24 h",
  "mod.rename": "Rename",
  "mod.ban3d": "Suspend 3 days",
  "mod.ban1y": "Ban",
  "mod.confirm": "Confirm?",
  "mod.result": "{action} · {when}",
  "mod.empty": "No reports here.",
  "mod.done": "Done.",
  "menu.moderation": "Moderation",
  "share.tagline": "Pokémon autochess in your browser",
  "share.ofN": "of {n}",
  "share.solo": "solo",
  "share.trainer": "Trainer",
  "share.team": "My team",
  "share.level": "Level {n}",
  "share.round": "Round {r}",
  "share.wins": "{n} wins",
  "share.cta": "Play free — no download",
  "share.text": "I placed #{place} of {n} in PokéChess! Play free: {url}",
  "share.button": "Share result",
  "share.downloaded": "Image saved!",
  "ach.primeira-partida.name": "First steps",
  "ach.primeira-partida.desc": "Play your first match.",
  "ach.partidas-10.name": "Rookie trainer",
  "ach.partidas-10.desc": "Play {n} matches.",
  "ach.partidas-100.name": "Veteran",
  "ach.partidas-100.desc": "Play {n} matches.",
  "ach.partidas-1000.name": "Legend of Kanto",
  "ach.partidas-1000.desc": "Play {n} matches.",
  "ach.primeira-vitoria.name": "First win",
  "ach.primeira-vitoria.desc": "Win a match against other players.",
  "ach.vitorias-10.name": "Champion",
  "ach.vitorias-10.desc": "Win {n} matches.",
  "ach.vitorias-50.name": "Unstoppable",
  "ach.vitorias-50.desc": "Win {n} matches.",
  "ach.top4-25.name": "Always on the podium",
  "ach.top4-25.desc": "Finish top 4 {n} times.",
  "ach.ranqueada-vitorias-10.name": "League ace",
  "ach.ranqueada-vitorias-10.desc": "Win {n} ranked matches.",
  "ach.master-ball.name": "Pokémon Master",
  "ach.master-ball.desc": "Reach Master Ball in a season.",
  "ach.dias-7.name": "A steady week",
  "ach.dias-7.desc": "Play {n} days in a row.",
  "ach.dias-30.name": "Faithful",
  "ach.dias-30.desc": "Play {n} days in a row.",
  "ach.missoes-50.name": "Mission runner",
  "ach.missoes-50.desc": "Complete {n} daily missions.",
  "ach.nivel-20.name": "Seasoned trainer",
  "ach.nivel-20.desc": "Reach account level {n}.",
  "ach.criadouro-1.name": "Daycare graduate",
  "ach.criadouro-1.desc": "Unlock a line in the Daycare.",
  "ach.pokedex-completa.name": "Professor",
  "ach.pokedex-completa.desc": "Unlock all {n} Daycare lines.",
  "ach.shinies-5.name": "Rare shine",
  "ach.shinies-5.desc": "Own {n} shinies.",
  "ach.shinies-25.name": "Shiny hunter",
  "ach.shinies-25.desc": "Own {n} shinies.",
  "ach.cartas-10.name": "Collector",
  "ach.cartas-10.desc": "Own {n} cards.",
  "ach.agua-7.name": "Water master",
  "ach.agua-7.desc": "Get 7 Water in a match.",
  "ach.voador-7.name": "Ruler of the skies",
  "ach.voador-7.desc": "Get 7 Flying in a match.",
  "ach.brigao-6.name": "Street brawl",
  "ach.brigao-6.desc": "Get 6 Brawlers in a match.",
  "ach.muralha-6.name": "Unshakable",
  "ach.muralha-6.desc": "Get 6 Walls in a match.",
  "ach.tres-estrelas-3.name": "Starry",
  "ach.tres-estrelas-3.desc": "Have three ★3 Pokémon at once.",
  "ach.vitoria-por-um-fio.name": "By a thread",
  "ach.vitoria-por-um-fio.desc": "Win with 5 HP or less.",
  "ach.nivel-9.name": "Max level",
  "ach.nivel-9.desc": "Reach level 9 in a match.",
  "ach.itens-6.name": "Arsenal",
  "ach.itens-6.desc": "Have 6 completed items on your board.",
  "ach.giovanni.name": "Beat Giovanni",
  "ach.giovanni.desc": "Clear a solo match through 8-7.",
  "ach.dragonite-3.name": "Legendary dragon",
  "ach.dragonite-3.desc": "Make a ★3 Dragonite.",
  "ach.magikarp-3.name": "Splash!",
  "ach.magikarp-3.desc": "Make a ★3 Magikarp.",
  "ach.so-custo-1.name": "Humble",
  "ach.so-custo-1.desc": "Win using only cost-1 Pokémon.",
  "ach.sem-vitoria-top4.name": "Survivor",
  "ach.sem-vitoria-top4.desc": "Finish top 4 without winning a fight.",
  "ach.mewtwo.name": "Mighty clone",
  "ach.mewtwo.desc": "Field a Mewtwo.",
  "tut.coach": "Professor Oak",
  "tut.next": "Next step",
  "tut.shop": "This is the shop. Tap a Pokémon to buy it: the number is its gold cost.",
  "tut.place": "It went to your bench. Drag it onto the board: only Pokémon on the board fight.",
  "tut.fight": "Ready? Tap Fight! Battles are automatic.",
  "tut.watch": "Your Pokémon fight on their own. Winning gives gold and items.",
  "tut.combine": "3 identical Pokémon become ★2, much stronger. The ones you own glow in the shop.",
  "tut.traits": "Pokémon of the same type activate synergies. Check Synergies to see what's active.",
  "tut.items": "You got a component! Tap it, then a Pokémon, to equip it. Two components make an item.",
  "tut.xp": "Buy XP to level up: each level lets you place one more Pokémon on the board.",
  "tut.interest": "Pro tip: saving gold earns interest, +1 per 10 (up to +5) each round.",
  "tut.end": "Your turn! Win this fight to finish the tutorial.",
  "tut.fightBtn": "Fight!",
  "tut.offerTitle": "New here?",
  "tut.offer": "Learn to play in 3 minutes with a guided match and earn ₽ {n}.",
  "tut.start": "Start tutorial",
  "tut.skip": "I know how to play",
  "tut.done": "Tutorial complete!",
  "menu.tutorial": "Tutorial",
  "menu.tutorial.sub": "Learn in 3 minutes",
  "lobby.fillBots": "Fill with bots",
  "lobby.fillBotsHint": "Empty seats become game-controlled trainers.",
};
export default en;
